import { FC, useState } from 'react';

import { AutoComplete, AutoCompleteProps, Input } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import debounce from 'lodash/debounce';

import { getLocationFromDadata } from '@/api/dadata';
import useAsyncEffect from '@/hooks/use-async-effect';

import styles from './LocationSelect.module.scss';

type Bound =
	| undefined
	| 'country'
	| 'region'
	| 'area'
	| 'city'
	| 'settlement'
	| 'street'
	| 'house'
	| 'street-flat'
	| 'house-flat'; //value for dadata https://confluence.hflabs.ru/pages/viewpage.action?pageId=222888017

type Props = {
	fromBound?: Bound;
	toBound?: Bound;
	valueField?: 'value' | 'unrestricted_value';
	inputArea?: 'input' | 'textarea';
} & AutoCompleteProps;

export const LocationSelect: FC<Props> = ({
	toBound = 'house',
	fromBound = undefined,
	valueField = 'unrestricted_value',
	inputArea = 'textarea',
	...autoCompleteProps
}) => {
	const [inputValue, setInputValue] = useState('');
	const [options, setOptions] = useState<AutoCompleteProps['options']>([]);

	const { TextArea } = Input;
	const handleGetLocation = debounce(async (e: string) => {
		setInputValue(e);
	}, 400);

	const handleBlur = () => {
		if (!options.length) {
			autoCompleteProps.onChange(null);
			return;
		}

		// Пушим первый элемент из списка, если пользователь что-то ввел, но не выбрал значение
		if (inputValue && !options.find((option) => option.value === autoCompleteProps.value)) {
			const firstOption = options[0];

			if (autoCompleteProps.onChange) {
				autoCompleteProps.onChange(firstOption.value as string, firstOption);
			}
		}
	};

	useAsyncEffect(async () => {
		try {
			const fromParams = fromBound
				? {
						from_bound: {
							value: fromBound,
						},
					}
				: {};

			const { suggestions } = await getLocationFromDadata({
				query: inputValue,
				to_bound: {
					value: toBound,
				},
				...fromParams,
			});

			const selectOptions = suggestions
				.filter((item) => item.data.geo_lat && item.data.geo_lon)
				.map<DefaultOptionType>((item) => ({
					label: <div className={styles.options}>{item[valueField]}</div>,
					value: item[valueField],
					additionalInfo: item.data,
				}));

			setOptions(selectOptions);
		} catch (e) {
			console.error(e);
		}
	}, [inputValue]);

	return (
		<div className={styles.container}>
			<AutoComplete
				showSearch={{ onSearch: handleGetLocation }}
				onBlur={handleBlur}
				options={options}
				{...autoCompleteProps}
				size="large"
			>
				{inputArea === 'textarea' ? (
					<TextArea size="large" autoSize={{ minRows: 1, maxRows: 4 }} />
				) : (
					<Input size="large" />
				)}
			</AutoComplete>
		</div>
	);
};
